import {
  CREDIT_RATES,
  MAX_INSTRUCTION_NAME_LENGTH,
  MAX_SAVED_INSTRUCTIONS,
  MAX_SAVED_INSTRUCTION_LENGTH,
  countWords,
  type SavedInstruction,
  type SavedInstructionInput,
} from '@lyrmo/shared';
import { useEffect, useId, useState } from 'react';
import { Button } from '../../components/Button';
import type { Messages } from '../../i18n/en';
import { useI18n } from '../../i18n/i18n-context';
import { instructionItems } from '../rewrite/menu-items';
import { useAccount } from './account-context';
import { AccountFrame } from './AccountFrame';
import {
  ConfirmDelete,
  ItemHead,
  ItemSection,
  ItemSettings,
  ManagerLayout,
  ManagerList,
  OwnActions,
  TrySample,
} from './ItemManager';
import { useTrials } from './useTrials';
// Its form is laid out like the tones dialog's, with its styles.
import './tones.css';

/** What's wrong with the instruction, in the writer's terms, before the server says so. */
function problemWith(
  { name, text }: SavedInstructionInput,
  others: readonly SavedInstruction[],
  t: Messages,
): string | null {
  if (!name) return t.instructions.nameMissing;
  if (others.some((other) => other.name.toLowerCase() === name.toLowerCase())) {
    return t.instructions.nameTaken(name);
  }
  if (!text) return t.instructions.textMissing;
  return null;
}

interface InstructionFormProps {
  /** The instruction to change, or null to save a new one. */
  readonly instruction: SavedInstruction | null;
  /**
   * What the form starts with: the instruction's own, a duplicate's, or an instruction typed
   * for one rewrite.
   */
  readonly initial: SavedInstructionInput;
  /** The writer's other instructions, whose names this one can't take. */
  readonly others: readonly SavedInstruction[];
  readonly onSaved: (instruction: SavedInstruction) => void;
  readonly onCancel: () => void;
}

function InstructionForm({
  instruction,
  initial,
  others,
  onSaved,
  onCancel,
}: InstructionFormProps) {
  const { t, describeError } = useI18n();
  const { client } = useAccount();
  const [draft, setDraft] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ids = { name: useId(), text: useId() };

  const update = (changes: Partial<SavedInstructionInput>) => {
    setDraft({ ...draft, ...changes });
    setError(null);
  };

  async function save() {
    const input = { name: draft.name.trim(), text: draft.text.trim() };
    const problem = problemWith(input, others, t);
    if (problem) {
      setError(problem);
      return;
    }
    setSaving(true);
    try {
      onSaved(
        instruction
          ? await client.updateSavedInstruction(instruction.id, input)
          : await client.createSavedInstruction(input),
      );
    } catch (caught) {
      setError(describeError(caught));
      setSaving(false);
    }
  }

  return (
    <form
      className="tone-form"
      onSubmit={(event) => {
        event.preventDefault();
        void save();
      }}
    >
      <h3 className="item-name">
        {instruction ? t.menus.editItem(instruction.name) : t.instructions.newInstruction}
      </h3>
      <div className="tone-field">
        <label className="field-label" htmlFor={ids.name}>
          {t.instructions.name}
        </label>
        <input
          id={ids.name}
          className="text-input"
          required
          maxLength={MAX_INSTRUCTION_NAME_LENGTH}
          placeholder={t.instructions.namePlaceholder}
          value={draft.name}
          onChange={(event) => {
            update({ name: event.target.value });
          }}
        />
      </div>

      <div className="tone-field">
        <label className="field-label" htmlFor={ids.text}>
          {t.instructions.text}
        </label>
        <textarea
          id={ids.text}
          className="text-input tone-notes"
          rows={4}
          required
          maxLength={MAX_SAVED_INSTRUCTION_LENGTH}
          placeholder={t.instructions.textPlaceholder}
          value={draft.text}
          onChange={(event) => {
            update({ text: event.target.value });
          }}
        />
        <p className="field-hint">
          {draft.text.length} / {MAX_SAVED_INSTRUCTION_LENGTH}
        </p>
      </div>

      {error && (
        <p className="dialog-error" role="alert">
          {error}
        </p>
      )}
      <div className="form-actions">
        <Button onClick={onCancel}>{t.common.cancel}</Button>
        <Button type="submit" variant="primary" disabled={saving}>
          {saving ? t.instructions.saving : t.instructions.save}
        </Button>
      </div>
    </form>
  );
}

/** What the right of the dialog shows: an instruction, or the form saving or changing one. */
type Pane =
  /** The instruction shown, or null for the first there is. */
  | { readonly kind: 'instruction'; readonly id: string | null }
  | {
      readonly kind: 'form';
      readonly instruction: SavedInstruction | null;
      readonly draft: SavedInstructionInput;
      /** The instruction to show again on Cancel. */
      readonly back: string | null;
    };

/**
 * The writer's saved instructions: whether the instruction menu shows each, whether it's
 * pinned, what it tells rewrites, and a rewrite of a sample following it. They're saved,
 * changed, duplicated, and deleted here.
 */
function InstructionManager() {
  const { t, format, describeError } = useI18n();
  const {
    client,
    savedInstructions,
    reloadSavedInstructions,
    menuSettings,
    reloadMenuSettings,
    keepMenuItem,
    instructionDraft,
    closeDialog,
  } = useAccount();
  // An instruction typed for one rewrite, being saved, starts a new one with its text.
  const [pane, setPane] = useState<Pane>(() =>
    instructionDraft
      ? {
          kind: 'form',
          instruction: null,
          draft: { name: '', text: instructionDraft.text },
          back: null,
        }
      : { kind: 'instruction', id: null },
  );
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { trials, tryIt, forget } = useTrials();
  const own = savedInstructions ?? [];
  const settings = menuSettings.instructions;
  const items = instructionItems(own, settings);
  const full = savedInstructions !== null && savedInstructions.length >= MAX_SAVED_INSTRUCTIONS;

  // Opening the dialog brings the list up to date, as instructions can change in another tab.
  useEffect(() => {
    Promise.all([reloadSavedInstructions(), reloadMenuSettings('instructions')]).catch(
      (caught: unknown) => {
        setLoadError(describeError(caught));
      },
    );
  }, [describeError, reloadSavedInstructions, reloadMenuSettings]);

  const show = (id: string | null) => {
    setPane({ kind: 'instruction', id });
    setSaved(null);
    setDeleting(false);
    setError(null);
  };

  const edit = (
    instruction: SavedInstruction | null,
    draft: SavedInstructionInput,
    back: string | null,
  ) => {
    setPane({ kind: 'form', instruction, draft, back });
    setSaved(null);
  };

  async function remove(instruction: SavedInstruction) {
    setBusy(true);
    setError(null);
    try {
      await client.deleteSavedInstruction(instruction.id);
      await Promise.all([reloadSavedInstructions(), reloadMenuSettings('instructions')]);
      show(null);
    } catch (caught) {
      setError(describeError(caught));
    } finally {
      setBusy(false);
    }
  }

  const selectedId = pane.kind === 'instruction' ? pane.id : (pane.instruction?.id ?? null);
  // The one shown, or the first there is; deleted meanwhile, as in another tab, the first too.
  const shownItem =
    pane.kind === 'instruction'
      ? (items.find((candidate) => candidate.id === pane.id) ?? items[0])
      : undefined;

  const details = () => {
    if (pane.kind === 'form') {
      const fromDraft = !pane.instruction && pane.back === null && instructionDraft;
      return (
        <InstructionForm
          // A fresh form for each instruction, or duplicate, it's opened for.
          key={`${pane.instruction?.id ?? 'new'}:${pane.draft.name}`}
          instruction={pane.instruction}
          initial={pane.draft}
          others={own.filter((other) => other.id !== pane.instruction?.id)}
          onSaved={(instruction) => {
            // Its try, if any, followed what it said before.
            forget(instruction.id);
            const showSaved = () => {
              show(instruction.id);
              setSaved(instruction.name);
            };
            const failed = (caught: unknown) => {
              showSaved();
              setError(describeError(caught));
            };
            reloadSavedInstructions().then(() => {
              if (!fromDraft) {
                showSaved();
                return;
              }
              // Saved from the rewrite it was typed for, it's followed there from now on: once
              // the menu has it, which otherwise drops what it doesn't have.
              instructionDraft.onSaved?.(instruction);
              closeDialog();
            }, failed);
          }}
          onCancel={() => {
            if (fromDraft) closeDialog();
            else show(pane.instruction?.id ?? pane.back);
          }}
        />
      );
    }

    if (!shownItem) {
      return savedInstructions === null ? null : (
        <p className="manager-empty">{t.instructions.empty}</p>
      );
    }
    const item = shownItem;
    const instruction = own.find((candidate) => candidate.id === item.id);
    if (!instruction) return null;
    const credits = countWords(t.menus.sample) * CREDIT_RATES.rewritePerWord;
    return (
      <div className="item-details" key={item.id}>
        {saved && (
          <p className="tones-saved" role="status">
            {t.instructions.saved(saved)}
          </p>
        )}
        <ItemHead
          item={item}
          actions={
            <OwnActions
              name={instruction.name}
              full={full}
              busy={busy}
              onEdit={() => {
                edit(instruction, instruction, instruction.id);
              }}
              onDuplicate={() => {
                edit(
                  null,
                  {
                    name: t.menus.copyName(instruction.name).slice(0, MAX_INSTRUCTION_NAME_LENGTH),
                    text: instruction.text,
                  },
                  instruction.id,
                );
              }}
              onDelete={() => {
                setDeleting(true);
              }}
            />
          }
        />
        {deleting && (
          <ConfirmDelete
            message={t.instructions.confirmDelete(instruction.name)}
            busy={busy}
            onCancel={() => {
              setDeleting(false);
            }}
            onConfirm={() => void remove(instruction)}
          />
        )}
        {full && <p className="field-hint">{t.instructions.limit(MAX_SAVED_INSTRUCTIONS)}</p>}

        <ItemSettings
          item={item}
          ready={settings !== null}
          onKeep={(change) => {
            setError(null);
            keepMenuItem('instructions', {
              id: item.id,
              hidden: item.hidden,
              pinned: item.pinned,
              ...change,
            }).catch((caught: unknown) => {
              setError(describeError(caught));
            });
          }}
        />
        {error && (
          <p className="dialog-error" role="alert">
            {error}
          </p>
        )}

        <ItemSection title={t.instructions.text}>
          <p className="item-text">{instruction.text}</p>
        </ItemSection>

        <TrySample
          trial={trials[item.id]}
          cost={`${format.number(credits)}${t.account.chipUnit(credits)}`}
          onTry={() =>
            void tryIt(item.id, async (signal) => {
              const { rewrites, usage } = await client.createRewrite(t.menus.sample, {
                signal,
                options: {
                  tone: 'natural',
                  length: 'same',
                  language: 'auto',
                  savedInstructions: [item.id],
                },
              });
              return { text: rewrites.optimalVersion, usage };
            })
          }
        />
      </div>
    );
  };

  return (
    <ManagerLayout
      shown={items.filter((item) => !item.hidden).length}
      total={items.length}
      list={
        <ManagerList
          groups={[
            {
              label: t.options.savedInstructions,
              items,
              aside:
                savedInstructions &&
                t.menus.ownCount(savedInstructions.length, MAX_SAVED_INSTRUCTIONS),
              empty:
                savedInstructions === null ? (
                  loadError ? (
                    <p className="dialog-error" role="alert">
                      {loadError}
                    </p>
                  ) : (
                    <p className="manager-empty" role="status">
                      {t.instructions.loading}
                    </p>
                  )
                ) : (
                  <p className="manager-empty">{t.options.noSaved}</p>
                ),
            },
          ]}
          selected={shownItem?.id ?? selectedId}
          onSelect={show}
          newLabel={t.instructions.newInstruction}
          newDisabled={savedInstructions === null || full}
          onNew={() => {
            edit(null, { name: '', text: '' }, shownItem?.id ?? null);
          }}
        />
      }
      details={details()}
    />
  );
}

export function InstructionsDialog() {
  const { t } = useI18n();
  const { account, dialog, closeDialog, openDialog } = useAccount();
  return (
    <AccountFrame
      open={dialog === 'instructions'}
      onClose={closeDialog}
      title={t.instructions.title}
      wide={Boolean(account?.user)}
    >
      {account?.user ? (
        <InstructionManager />
      ) : (
        <>
          <p className="dialog-intro">{t.instructions.pitch}</p>
          <div className="form-actions">
            <Button onClick={closeDialog}>{t.common.notNow}</Button>
            <Button
              variant="primary"
              onClick={() => {
                openDialog('sign-in');
              }}
            >
              {t.common.signIn}
            </Button>
          </div>
        </>
      )}
    </AccountFrame>
  );
}
